import {Box, Flex, Image, Text, Title} from 'exsportia-components'
import React, {FC, useMemo, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'

import {useStoreProduct} from '../../api/store'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {Chevron} from '../../ui/components/chevron/chevron'
import {useStoreCart} from '../../services/store/cart'
import {Button} from '../../ui/components/button/button'
import {useLocale} from '../../utils/i18n'
import {useModalActions} from '../../utils/modals'
import {matchVariant, useCurrencyFormat} from './store-shared'

// exsportia-components' Box and Flex support `overflow` and nothing axis-specific — the string
// "overflowX" does not appear anywhere in the built package — so an `overflowX`/`overflowY` PROP is
// dropped in silence. Passed through `style`, which does reach the DOM (Dima 2026-08-30).
const scrollXStyle: React.CSSProperties = {overflowX: 'auto'}

export const StoreProductPage: FC = () => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const format = useCurrencyFormat()
  const {venueId, stockItemId} = useParams<{venueId: string; stockItemId: string}>()
  const {data: product, isLoading} = useStoreProduct(venueId, stockItemId)
  const cart = useStoreCart(venueId)
  const {openModal, closeAllModals} = useModalActions()
  const [selection, setSelection] = useState<Record<string, string>>({})
  const [imageIndex, setImageIndex] = useState<number | null>(null)

  const matched = useMemo(
    () => (product?.hasVariants && product.options && product.variants
      ? matchVariant(product.options, product.variants, selection)
      : null),
    [product, selection],
  )

  if (isLoading || !product) {
    return null
  }

  const price = product.hasVariants ? (matched ? matched.variant.price : null) : product.price ?? null
  const compareAt = product.hasVariants
    ? matched?.variant.compareAtPrice ?? null
    : product.compareAtPrice ?? null
  const available = product.hasVariants ? (matched ? matched.variant.available : true) : product.inStock
  const canAdd = product.hasVariants ? Boolean(matched?.variant.available) : product.inStock
  const matchedImageIndex = matched?.variant.imageIndex ?? null
  const shownImage = imageIndex !== null
    ? product.media[imageIndex]
    : matchedImageIndex !== null
    ? product.media[matchedImageIndex]
    : product.media[product.thumbnail] || product.media[0]

  const addToCart = (silent = false) => {
    if (!canAdd || !stockItemId) {
      return
    }
    cart.add({
      stockItemId,
      variantId: matched ? matched.variantId : null,
      name: product.name,
      variantLabel: matched ? matched.variant.label : null,
      price: price || 0,
      thumbUrl: shownImage || null,
    })
    if (silent) {
      return
    }
    // A toast was too easy to miss — adding to the cart now answers "what
    // next?" explicitly (Dima 2026-08-17).
    openModal({
      title: l('store-added-to-cart', {caseAction: 'capitalize'}),
      footerConfig: {disabled: true},
      component: (
        <Flex
          gap='12px'
          pb='8px'
          width='100%'
          flexDirection='column'
        >
          <Text
            text={`${product.name}${matched ? ` · ${matched.variant.label}` : ''}`}
            settings={{fontSize: '14px', color: 'rgba(0, 16, 61, 0.48)'}}
          />
          <Flex
            gap='8px'
            flexDirection={['column', 'row']}
          >
            <Button
              type='secondary'
              onClick={() => {
                closeAllModals()
                navigate('../store')
              }}
            >
              {l('store-continue-shopping', {caseAction: 'capitalize'})}
            </Button>
            <Button
              type='primary'
              onClick={() => {
                closeAllModals()
                navigate('../store/cart')
              }}
            >
              {l('store-go-to-cart', {caseAction: 'capitalize'})}
            </Button>
          </Flex>
        </Flex>
      ),
    })
  }

  return (
    <GridLayout
      mainFirstOnMobile
      header={
        <Flex
          p='16px'
          alignItems='center'
          justifyContent='space-between'
        >
          {/* The band is the way back: chevron + "Store" both return to the
              catalogue (Dima 2026-08-19). */}
          <Flex
            gap='8px'
            alignItems='center'
            cursor='pointer'
            onClick={() => navigate('../store')}
          >
            <Chevron
              direction={'left'}
              styleType={'simple'}
            />
            <Title
              styleType='h3'
              text={l('store-title', {caseAction: 'capitalize'})}
            />
          </Flex>
          {/* The cart must be reachable from here — a product page with no way
              to the cart strands whatever was just added (Dima 2026-08-17). */}
          <Button
            type='secondary'
            settings={{width: 'max-content'}}
            onClick={() => navigate('../store/cart')}
          >
            {`${l('store-cart', {caseAction: 'capitalize'})}${cart.count ? ` (${cart.count})` : ''}`}
          </Button>
        </Flex>
      }
    >
    {/* Box a: gallery + description (main column). */}
    <Box width='100%'>
      <Box
        width='100%'
        pt='100%'
        position='relative'
        borderRadius='16px'
        overflow='hidden'
        backgroundColor='rgba(0, 16, 61, 0.06)'
      >
        {shownImage && (
          <Box
            position='absolute'
            top='0'
            left='0'
            width='100%'
            height='100%'
          >
            <Image
              width='100%'
              height='100%'
              style={{objectFit: 'contain'}}
              src={shownImage}
            />
          </Box>
        )}
      </Box>
      {product.media.length > 1 && (
        <Flex
          mt='8px'
          gap='8px'
          style={scrollXStyle}
        >
          {product.media.map((url, index) => (
            <Box
              key={url}
              width='56px'
              height='56px'
              flexShrink={0}
              borderRadius='12px'
              overflow='hidden'
              cursor='pointer'
              border={index === (imageIndex ?? product.thumbnail) ? '2px solid #EC1E5F' : 'none'}
              onClick={() => setImageIndex(index)}
            >
              <Image
                width='100%'
                height='100%'
                style={{objectFit: 'cover'}}
                src={url}
              />
            </Box>
          ))}
        </Flex>
      )}
      {product.description && (
        <Box mt='24px'>
          <Title
            styleType='h3'
            text={l('store-description', {caseAction: 'capitalize'})}
          />
          <Box
            mt='8px'
            whiteSpace='pre-line'
          >
            {product.description}
          </Box>
        </Box>
      )}
    </Box>
    {/* Box b: the buy box (side column on desktop, first on mobile). */}
    <Box
      width='100%'
      p='16px'
    >
      <Box>
        <Title
          styleType='h1'
          text={product.name}
        />
      </Box>
      <Flex
        mt='8px'
        gap='8px'
        alignItems='center'
      >
        {compareAt !== null && price !== null && compareAt > price && (
          <Text
            text={format(compareAt)}
            settings={{textDecoration: 'line-through', color: 'rgba(0, 16, 61, 0.48)'}}
          />
        )}
        {price !== null && (
          <Title
            styleType='h2'
            text={format(price)}
          />
        )}
        {!available && (
          <Text
            text={l('store-sold-out', {caseAction: 'capitalize'})}
            settings={{color: '#B3261E', fontWeight: 600}}
          />
        )}
      </Flex>
      {product.hasVariants && (product.options || []).map((option) => (
        <Box
          key={option.name}
          mt='16px'
        >
          <Text
            text={option.name}
            settings={{color: 'rgba(0, 16, 61, 0.48)', fontSize: '14px'}}
          />
          <Flex
            mt='8px'
            gap='8px'
            flexWrap='wrap'
          >
            {option.values.map((value) => (
              <Button
                key={value}
                type={selection[option.name] === value ? 'primary' : 'secondary'}
                settings={{width: 'max-content'}}
                onClick={() => setSelection({...selection, [option.name]: value})}
              >
                {value}
              </Button>
            ))}
          </Flex>
        </Box>
      ))}
      {product.hasVariants && !matched && (
        <Box mt='8px'>
          <Text
            text={l('store-select-options', {caseAction: 'capitalize'})}
            settings={{fontSize: '13px', color: 'rgba(0, 16, 61, 0.48)'}}
          />
        </Box>
      )}
      <Flex
        mt='24px'
        gap='8px'
        flexDirection={['column', 'row', 'row', 'row']}
      >
        <Button
          type='primary'
          disabled={!canAdd}
          onClick={() => addToCart()}
        >
          {l('store-add-to-cart', {caseAction: 'capitalize'})}
        </Button>
        <Button
          type='secondary'
          disabled={!canAdd}
          onClick={() => {
            addToCart(true)
            navigate('../store/cart')
          }}
        >
          {l('store-buy-now', {caseAction: 'capitalize'})}
        </Button>
      </Flex>
    </Box>
    </GridLayout>
  )
}
